import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Popconfirm,
  Radio,
  Tabs,
  Image,
  Typography,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;

// 生成的内容（纯内容，不指定平台和模式）
interface GeneratedContent {
  id: number;
  title: string;
  contentType: string;
  outputFormat: 'video' | 'image' | 'text' | 'audio';
  content: string; // 内容URL或文本
  thumbnail?: string; // 缩略图
  status: 'pending' | 'approved' | 'rejected';
  generateTime: string;
  reviewTime?: string;
  reviewer?: string;
  hasAd: boolean; // 是否包含广告
  adQrCode?: string; // 广告二维码图片
  adLink?: string; // 广告链接
  // 关联的发布记录
  publishes?: ContentPublish[];
}

// 内容发布记录（审核时选择平台和模式）
interface ContentPublish {
  id: number;
  contentId: number; // 关联的内容ID
  platform: string; // 发布平台
  publishMode: 'publish' | 'comment' | 'live'; // 发布模式：发布、评论、直播
  status: 'pending' | 'approved' | 'rejected' | 'published';
  publishTime?: string;
  reviewer?: string;
  // 评论相关字段
  targetType?: 'live_room' | 'post' | 'video'; // 评论目标类型：直播间、帖子、视频
  targetId?: string; // 目标ID
  targetTitle?: string; // 目标标题（如直播间名称、帖子标题）
  targetUrl?: string; // 目标链接
  commentPosition?: 'top' | 'hot' | 'latest'; // 评论位置：置顶、热门、最新
  // 直播相关字段
  liveTitle?: string; // 直播标题
  liveDuration?: number; // 直播时长（分钟）
  liveStartTime?: string; // 直播开始时间
  liveEndTime?: string; // 直播结束时间
  liveViewers?: number; // 直播观看人数
  performance?: {
    views?: number;
    likes?: number;
    comments?: number;
    shares?: number;
    adImpressions?: number; // 广告展示次数
    adClicks?: number; // 广告点击次数
    adConversions?: number; // 广告转化次数
    // 评论相关统计
    commentLikes?: number; // 评论点赞数
    commentReplies?: number; // 评论回复数
    commentViews?: number; // 评论查看数
    // 直播相关统计
    livePeakViewers?: number; // 直播峰值观看人数
    liveGifts?: number; // 直播礼物数
    liveRevenue?: number; // 直播收入
  };
}

// Demo数据 - 待审核（纯内容，不指定平台和模式）
const pendingData: GeneratedContent[] = [
  {
    id: 1,
    title: '抖奶擦边视频-001',
    contentType: 'bounce_edge_video',
    outputFormat: 'video',
    content: 'https://example.com/videos/bounce_001.mp4',
    thumbnail: 'https://via.placeholder.com/200x300',
    status: 'pending',
    generateTime: '2025-12-04 09:00:00',
    hasAd: true,
    adQrCode: 'https://via.placeholder.com/200x200',
    adLink: 'https://example.com/promotion?ref=001',
  },
  {
    id: 2,
    title: '今日足球对战分析-曼城vs利物浦',
    contentType: 'football_analysis_video',
    outputFormat: 'video',
    content: 'https://example.com/videos/football_001.mp4',
    thumbnail: 'https://via.placeholder.com/300x200',
    status: 'pending',
    generateTime: '2025-12-04 08:00:00',
    hasAd: false,
  },
  {
    id: 3,
    title: '美女直播预告图-小美',
    contentType: 'beauty_live_preview',
    outputFormat: 'image',
    content: 'https://example.com/images/beauty_001.jpg',
    thumbnail: 'https://via.placeholder.com/200x300',
    status: 'pending',
    generateTime: '2025-12-04 10:00:00',
    hasAd: false,
  },
  {
    id: 4,
    title: '游戏攻略-王者荣耀上分技巧',
    contentType: 'game_strategy_article',
    outputFormat: 'text',
    content: '王者荣耀上分技巧：1. 选择强势英雄...',
    status: 'pending',
    generateTime: '2025-12-04 11:00:00',
    hasAd: true,
    adLink: 'https://example.com/game-download?ref=004',
  },
  {
    id: 5,
    title: 'AI直播内容-美女聊天',
    contentType: 'live_content',
    outputFormat: 'video',
    content: 'https://example.com/live/live_001.mp4',
    thumbnail: 'https://via.placeholder.com/200x300',
    status: 'pending',
    generateTime: '2025-12-04 12:00:00',
    hasAd: false,
  },
];

// Demo数据 - 已审核通过的内容（包含关联的发布记录）
const approvedData: GeneratedContent[] = [
  {
    id: 101,
    title: '抖奶擦边视频-001',
    contentType: 'bounce_edge_video',
    outputFormat: 'video',
    content: 'https://example.com/videos/bounce_001.mp4',
    thumbnail: 'https://via.placeholder.com/200x300',
    status: 'approved',
    generateTime: '2025-12-03 09:00:00',
    reviewTime: '2025-12-03 10:00:00',
    reviewer: '运营-张三',
    hasAd: true,
    adQrCode: 'https://via.placeholder.com/200x200',
    adLink: 'https://example.com/promotion?ref=101',
    publishes: [
      {
        id: 1001,
        contentId: 101,
        platform: 'TikTok',
        publishMode: 'publish',
        status: 'published',
        publishTime: '2025-12-03 11:00:00',
        reviewer: '运营-张三',
        performance: {
          views: 125000,
          likes: 8500,
          comments: 1200,
          shares: 560,
          adImpressions: 125000,
          adClicks: 12500,
          adConversions: 1250,
        },
      },
      {
        id: 1002,
        contentId: 101,
        platform: 'Instagram',
        publishMode: 'publish',
        status: 'published',
        publishTime: '2025-12-03 12:00:00',
        reviewer: '运营-张三',
        performance: {
          views: 98000,
          likes: 7200,
          comments: 980,
          shares: 420,
          adImpressions: 98000,
          adClicks: 9800,
          adConversions: 980,
        },
      },
    ],
  },
  {
    id: 102,
    title: '今日足球对战分析-曼城vs利物浦',
    contentType: 'football_analysis_video',
    outputFormat: 'video',
    content: 'https://example.com/videos/football_001.mp4',
    thumbnail: 'https://via.placeholder.com/300x200',
    status: 'approved',
    generateTime: '2025-12-03 08:00:00',
    reviewTime: '2025-12-03 09:00:00',
    reviewer: '运营-李四',
    hasAd: false,
    publishes: [
      {
        id: 1003,
        contentId: 102,
        platform: 'YouTube',
        publishMode: 'publish',
        status: 'published',
        publishTime: '2025-12-03 10:00:00',
        reviewer: '运营-李四',
        performance: {
          views: 89000,
          likes: 6200,
          comments: 890,
          shares: 320,
        },
      },
      {
        id: 1004,
        contentId: 102,
        platform: 'TikTok',
        publishMode: 'comment',
        status: 'published',
        publishTime: '2025-12-03 11:00:00',
        reviewer: '运营-李四',
        targetType: 'video',
        targetId: 'video_12345',
        targetTitle: '热门足球视频',
        targetUrl: 'https://tiktok.com/@user/video/12345',
        commentPosition: 'hot',
        performance: {
          commentLikes: 320,
          commentReplies: 45,
          commentViews: 8500,
        },
      },
    ],
  },
  {
    id: 103,
    title: 'AI直播内容-美女聊天',
    contentType: 'live_content',
    outputFormat: 'video',
    content: 'https://example.com/live/live_001.mp4',
    thumbnail: 'https://via.placeholder.com/200x300',
    status: 'approved',
    generateTime: '2025-12-03 10:00:00',
    reviewTime: '2025-12-03 11:00:00',
    reviewer: '运营-王五',
    hasAd: false,
    publishes: [
      {
        id: 1005,
        contentId: 103,
        platform: 'Bigo Live',
        publishMode: 'live',
        status: 'published',
        publishTime: '2025-12-03 20:00:00',
        reviewer: '运营-王五',
        liveTitle: 'AI美女聊天直播',
        liveDuration: 120,
        liveStartTime: '2025-12-03 20:00:00',
        liveEndTime: '2025-12-03 22:00:00',
        liveViewers: 5000,
        performance: {
          views: 5000,
          likes: 3500,
          comments: 1200,
          shares: 280,
          livePeakViewers: 5200,
          liveGifts: 1500,
          liveRevenue: 5000,
        },
      },
    ],
  },
];

const PLATFORMS = [
  'Facebook', 'TikTok', 'YouTube', 'Instagram', 'Shopee', 'Zalo', 'Lazada', 'Bigo Live', 'Tiki', 'TikTok Shop'
];

const ContentGenerationManagement: React.FC = () => {
  const [pendingList, setPendingList] = useState<GeneratedContent[]>(pendingData);
  const [approvedList, setApprovedList] = useState<GeneratedContent[]>(approvedData);
  const [activeTab, setActiveTab] = useState('pending');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState<GeneratedContent | null>(null);
  const [publishModalVisible, setPublishModalVisible] = useState(false);
  const [selectedContent, setSelectedContent] = useState<GeneratedContent | null>(null);
  const [searchText, setSearchText] = useState('');
  const [publishForm] = Form.useForm();

  // 模拟账号数据（实际应该从平台账号配置获取）
  const getPlatformAccounts = (platform: string, persona?: string) => {
    // 模拟账号数据，实际应该从PlatformAccountConfig获取
    const accounts = [
      { id: 1, platform: 'TikTok', persona: '美女主播', accountName: 'TikTok账号1' },
      { id: 2, platform: 'TikTok', persona: '纯评论', accountName: 'TikTok账号2' },
      { id: 3, platform: 'YouTube', persona: '体育专业分析', accountName: 'YouTube账号1' },
      { id: 4, platform: 'YouTube', persona: '纯评论', accountName: 'YouTube账号2' },
      { id: 5, platform: 'Facebook', persona: '体育专业分析', accountName: 'Facebook账号1' },
      { id: 6, platform: 'Bigo Live', persona: '美女主播', accountName: 'Bigo Live账号1' },
    ];
    return accounts.filter(acc => 
      acc.platform === platform && (!persona || acc.persona === persona)
    );
  };

  const generatePerformanceData = (mode: string, hasAd: boolean) => {
    const views = Math.floor(Math.random() * 100000);
    const adImpressions = hasAd ? views : 0;
    const adClicks = hasAd ? Math.floor(adImpressions * 0.1) : 0;
    const adConversions = hasAd ? Math.floor(adClicks * 0.1) : 0;
    
    if (mode === 'comment') {
      return {
        commentLikes: Math.floor(Math.random() * 500),
        commentReplies: Math.floor(Math.random() * 50),
        commentViews: Math.floor(Math.random() * 5000),
        adImpressions,
        adClicks,
        adConversions,
      };
    } else if (mode === 'live') {
      return {
        views,
        likes: Math.floor(Math.random() * 10000),
        comments: Math.floor(Math.random() * 2000),
        shares: Math.floor(Math.random() * 1000),
        livePeakViewers: Math.floor(Math.random() * 10000),
        liveGifts: Math.floor(Math.random() * 2000),
        liveRevenue: Math.floor(Math.random() * 10000),
      };
    } else {
      return {
        views,
        likes: Math.floor(Math.random() * 10000),
        comments: Math.floor(Math.random() * 2000),
        shares: Math.floor(Math.random() * 1000),
        adImpressions,
        adClicks,
        adConversions,
      };
    }
  };

  const handleApprove = (id: number) => {
    const item = pendingList.find(i => i.id === id);
    if (item) {
      setSelectedContent(item);
      publishForm.resetFields();
      setPublishModalVisible(true);
    }
  };

  const handlePublish = () => {
    publishForm.validateFields().then(values => {
      const item = selectedContent;
      if (item) {
        const platforms = values.platforms || [values.platform];
        const publishStrategy = values.publishStrategy || 'all';
        const selectedPersona = values.personaFilter;
        
        // 根据策略选择账号
        let selectedAccounts: any[] = [];
        platforms.forEach((platform: string) => {
          const accounts = getPlatformAccounts(platform, selectedPersona);
          if (publishStrategy === 'all') {
            selectedAccounts.push(...accounts);
          } else if (publishStrategy === 'random') {
            if (accounts.length > 0) {
              selectedAccounts.push(accounts[Math.floor(Math.random() * accounts.length)]);
            }
          } else if (publishStrategy === 'first') {
            if (accounts.length > 0) {
              selectedAccounts.push(accounts[0]);
            }
          }
        });

        // 为每个选中的账号创建发布记录
        const publishes: ContentPublish[] = selectedAccounts.map((account, index) => ({
          id: Date.now() + index,
          contentId: item.id,
          platform: account.platform,
          publishMode: values.publishMode,
          status: 'published',
          publishTime: new Date().toLocaleString('zh-CN'),
          reviewer: '当前用户',
          targetType: values.targetType,
          targetId: values.targetId,
          targetTitle: values.targetTitle,
          targetUrl: values.targetUrl,
          commentPosition: values.commentPosition,
          liveTitle: values.liveTitle,
          liveDuration: values.liveDuration,
          liveStartTime: values.liveStartTime,
          liveEndTime: values.liveEndTime,
          performance: generatePerformanceData(values.publishMode, item.hasAd),
        }));

        // 如果是新增审核，从待审核列表移除并添加到已审核列表
        if (item.status === 'pending') {
          setPendingList(prev => prev.filter(i => i.id !== item.id));
          const approvedItem: GeneratedContent = {
            ...item,
            status: 'approved',
            reviewTime: new Date().toLocaleString('zh-CN'),
            reviewer: '当前用户',
            publishes,
          };
          setApprovedList(prev => [...prev, approvedItem]);
        } else {
          // 如果是添加发布，更新已审核列表
          setApprovedList(prev =>
            prev.map(content =>
              content.id === item.id
                ? { ...content, publishes: [...(content.publishes || []), ...publishes] }
                : content
            )
          );
        }
        setPublishModalVisible(false);
        publishForm.resetFields();
        message.success(`审核通过，已发布到${publishes.length}个平台`);
      }
    });
  };

  const handleAddPublish = (contentId: number) => {
    const item = approvedList.find(i => i.id === contentId);
    if (item) {
      setSelectedContent(item);
      publishForm.resetFields();
      setPublishModalVisible(true);
    }
  };

  const handleReject = (id: number) => {
    setPendingList(prev => prev.map(item =>
      item.id === id
        ? { ...item, status: 'rejected', reviewTime: new Date().toLocaleString('zh-CN'), reviewer: '当前用户' }
        : item
    ));
    message.success('已拒绝');
  };

  const handlePreview = (record: GeneratedContent) => {
    setPreviewContent(record);
    setPreviewVisible(true);
  };

  const getStatusTag = (status: string) => {
    const map: Record<string, { text: string; color: string }> = {
      pending: { text: '待审核', color: 'orange' },
      approved: { text: '已通过', color: 'green' },
      rejected: { text: '已拒绝', color: 'red' },
      published: { text: '已发布', color: 'blue' },
    };
    const info = map[status] || { text: status, color: 'default' };
    return <Tag color={info.color}>{info.text}</Tag>;
  };

  const getFormatTag = (format: string) => {
    const map: Record<string, { text: string; color: string }> = {
      video: { text: '视频', color: 'red' },
      image: { text: '图片', color: 'blue' },
      text: { text: '文本', color: 'green' },
      audio: { text: '音频', color: 'orange' },
    };
    const info = map[format] || { text: format, color: 'default' };
    return <Tag color={info.color}>{info.text}</Tag>;
  };

  const getPublishModeTag = (mode: string) => {
    const map: Record<string, { text: string; color: string }> = {
      publish: { text: '发布', color: 'blue' },
      comment: { text: '评论', color: 'purple' },
      live: { text: '直播', color: 'red' },
    };
    const info = map[mode] || { text: mode, color: 'default' };
    return <Tag color={info.color}>{info.text}</Tag>;
  };

  const pendingColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: '内容类型',
      dataIndex: 'contentType',
      key: 'contentType',
      width: 150,
    },
    {
      title: '格式',
      dataIndex: 'outputFormat',
      key: 'outputFormat',
      width: 100,
      render: getFormatTag,
    },
    {
      title: '是否含广告',
      dataIndex: 'hasAd',
      key: 'hasAd',
      width: 100,
      render: (hasAd: boolean) => (
        <Tag color={hasAd ? 'orange' : 'default'}>
          {hasAd ? '含广告' : '不含广告'}
        </Tag>
      ),
    },
    {
      title: '生成时间',
      dataIndex: 'generateTime',
      key: 'generateTime',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right' as const,
      render: (_: any, record: GeneratedContent) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handlePreview(record)}>
            预览
          </Button>
          <Button type="link" icon={<CheckOutlined />} style={{ color: '#52c41a' }} onClick={() => handleApprove(record.id)}>
            审核发布
          </Button>
          <Popconfirm
            title="确认拒绝？"
            onConfirm={() => handleReject(record.id)}
          >
            <Button type="link" danger icon={<CloseOutlined />}>
              拒绝
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const approvedColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: '内容类型',
      dataIndex: 'contentType',
      key: 'contentType',
      width: 150,
    },
    {
      title: '格式',
      dataIndex: 'outputFormat',
      key: 'outputFormat',
      width: 100,
      render: getFormatTag,
    },
    {
      title: '关联发布数',
      key: 'publishCount',
      width: 120,
      render: (_: any, record: GeneratedContent) => (
        <span>{record.publishes?.length || 0}</span>
      ),
    },
    {
      title: '审核时间',
      dataIndex: 'reviewTime',
      key: 'reviewTime',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: GeneratedContent) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handlePreview(record)}>
            查看
          </Button>
          <Button type="link" icon={<PlusOutlined />} onClick={() => handleAddPublish(record.id)}>
            关联发布
          </Button>
        </Space>
      ),
    },
  ];

  const filteredPending = pendingList.filter(item =>
    item.title.includes(searchText) ||
    item.contentType.includes(searchText)
  );

  const filteredApproved = approvedList.filter(item =>
    item.title.includes(searchText) ||
    item.contentType.includes(searchText)
  );

  return (
    <div style={{ padding: 24 }}>
      <div className="page-header">
        <h1>内容生成管理</h1>
        <p>管理AI生成的内容，运营审核后可以发布</p>
      </div>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索标题、内容类型或平台"
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => message.info('刷新成功')}>
            刷新
          </Button>
        </Space>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={`待审核 (${pendingList.length})`} key="pending">
            <Table
              rowKey="id"
              dataSource={filteredPending}
              columns={pendingColumns}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1200 }}
            />
          </TabPane>
          <TabPane tab={`已审核 (${approvedList.length})`} key="approved">
            <Table
              rowKey="id"
              dataSource={filteredApproved}
              columns={approvedColumns}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1200 }}
              expandedRowRender={(record: GeneratedContent) => {
                if (!record.publishes || record.publishes.length === 0) {
                  return <div style={{ padding: 16 }}>暂无关联发布</div>;
                }
                return (
                  <Table
                    rowKey="id"
                    dataSource={record.publishes}
                    columns={[
                      { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
                      { title: '平台', dataIndex: 'platform', key: 'platform', width: 120 },
                      {
                        title: '发布模式',
                        dataIndex: 'publishMode',
                        key: 'publishMode',
                        width: 100,
                        render: getPublishModeTag,
                      },
                      { title: '发布时间', dataIndex: 'publishTime', key: 'publishTime', width: 180 },
                      {
                        title: '浏览量',
                        dataIndex: ['performance', 'views'],
                        key: 'views',
                        width: 100,
                        render: (views: number) => views?.toLocaleString() || '-',
                      },
                      {
                        title: '点赞数',
                        dataIndex: ['performance', 'likes'],
                        key: 'likes',
                        width: 100,
                        render: (likes: number) => likes?.toLocaleString() || '-',
                      },
                      {
                        title: '评论点赞',
                        dataIndex: ['performance', 'commentLikes'],
                        key: 'commentLikes',
                        width: 100,
                        render: (likes: number) => likes?.toLocaleString() || '-',
                      },
                      {
                        title: '直播观看',
                        dataIndex: 'liveViewers',
                        key: 'liveViewers',
                        width: 100,
                        render: (viewers: number) => viewers?.toLocaleString() || '-',
                      },
                      {
                        title: '广告点击',
                        dataIndex: ['performance', 'adClicks'],
                        key: 'adClicks',
                        width: 100,
                        render: (clicks: number) => clicks?.toLocaleString() || '-',
                      },
                    ]}
                    pagination={false}
                    size="small"
                  />
                );
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title="内容预览"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={800}
      >
        {previewContent && (
          <div>
            <p><strong>标题：</strong>{previewContent.title}</p>
            <p><strong>内容类型：</strong>{previewContent.contentType}</p>
            <p><strong>格式：</strong>{getFormatTag(previewContent.outputFormat)}</p>
            {previewContent.outputFormat === 'image' && (
              <Image src={previewContent.content} alt={previewContent.title} />
            )}
            {previewContent.outputFormat === 'video' && (
              <video src={previewContent.content} controls style={{ width: '100%' }} />
            )}
            {previewContent.outputFormat === 'text' && (
              <Text>{previewContent.content}</Text>
            )}
            {previewContent.outputFormat === 'text' && (
              <Text>{previewContent.content}</Text>
            )}
            {previewContent.publishes && previewContent.publishes.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4>关联发布记录</h4>
                {previewContent.publishes.map(publish => (
                  <div key={publish.id} style={{ marginTop: 8, padding: 12, background: '#f0f2f5', borderRadius: 4 }}>
                    <p><strong>平台：</strong>{publish.platform}</p>
                    <p><strong>发布模式：</strong>{getPublishModeTag(publish.publishMode)}</p>
                    {publish.publishTime && <p><strong>发布时间：</strong>{publish.publishTime}</p>}
                    {publish.targetTitle && <p><strong>目标：</strong>{publish.targetTitle}</p>}
                    {publish.liveTitle && <p><strong>直播标题：</strong>{publish.liveTitle}</p>}
                    {publish.performance && (
                      <div style={{ marginTop: 8 }}>
                        {publish.performance.views && <p><strong>浏览量：</strong>{publish.performance.views.toLocaleString()}</p>}
                        {publish.performance.commentLikes && <p><strong>评论点赞：</strong>{publish.performance.commentLikes.toLocaleString()}</p>}
                        {publish.performance.livePeakViewers && <p><strong>直播峰值观看：</strong>{publish.performance.livePeakViewers.toLocaleString()}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {previewContent.hasAd && (
              <div style={{ marginTop: 16, padding: 16, background: '#f5f5f5', borderRadius: 4 }}>
                <p><strong>是否含广告：</strong>是</p>
                {previewContent.adQrCode && (
                  <div style={{ marginTop: 8 }}>
                    <p><strong>广告二维码：</strong></p>
                    <Image src={previewContent.adQrCode} alt="广告二维码" width={200} />
                  </div>
                )}
                {previewContent.adLink && (
                  <div style={{ marginTop: 8 }}>
                    <p><strong>广告链接：</strong></p>
                    <a href={previewContent.adLink} target="_blank" rel="noopener noreferrer">
                      {previewContent.adLink}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={selectedContent?.status === 'pending' ? '审核发布' : '关联发布'}
        open={publishModalVisible}
        onCancel={() => {
          setPublishModalVisible(false);
          publishForm.resetFields();
        }}
        onOk={handlePublish}
        width={800}
      >
        <Form form={publishForm} layout="vertical">
          <Form.Item
            name="platforms"
            label="发布平台（可多选）"
            rules={[{ required: true, message: '请选择至少一个发布平台' }]}
          >
            <Select mode="multiple" placeholder="请选择发布平台">
              {PLATFORMS.map(platform => (
                <Option key={platform} value={platform}>{platform}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="publishStrategy"
            label="发布策略"
            rules={[{ required: true, message: '请选择发布策略' }]}
            initialValue="all"
          >
            <Radio.Group>
              <Radio value="all">全部账号</Radio>
              <Radio value="random">随机选择</Radio>
              <Radio value="first">第一个账号</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="personaFilter"
            label="账号人设筛选（可选）"
            tooltip="根据账号人设筛选账号，如：纯评论、美女主播、体育专业分析"
          >
            <Select placeholder="请选择账号人设（不选则不过滤）" allowClear>
              <Option value="纯评论">纯评论</Option>
              <Option value="美女主播">美女主播</Option>
              <Option value="体育专业分析">体育专业分析</Option>
              <Option value="游戏攻略">游戏攻略</Option>
              <Option value="生活分享">生活分享</Option>
              <Option value="搞笑娱乐">搞笑娱乐</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="publishMode"
            label="发布模式"
            rules={[{ required: true, message: '请选择发布模式' }]}
          >
            <Radio.Group>
              <Radio value="publish">发布</Radio>
              <Radio value="comment">评论</Radio>
              <Radio value="live">直播</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.publishMode !== currentValues.publishMode}
          >
            {({ getFieldValue }) => {
              const mode = getFieldValue('publishMode');
              if (mode === 'comment') {
                return (
                  <>
                    <Form.Item
                      name="targetType"
                      label="评论目标类型"
                      rules={[{ required: true, message: '请选择评论目标类型' }]}
                    >
                      <Radio.Group>
                        <Radio value="live_room">直播间</Radio>
                        <Radio value="post">帖子</Radio>
                        <Radio value="video">视频</Radio>
                      </Radio.Group>
                    </Form.Item>
                    <Form.Item name="targetId" label="目标ID">
                      <Input placeholder="请输入目标ID" />
                    </Form.Item>
                    <Form.Item name="targetTitle" label="目标标题">
                      <Input placeholder="请输入目标标题" />
                    </Form.Item>
                    <Form.Item name="targetUrl" label="目标链接">
                      <Input placeholder="请输入目标链接" />
                    </Form.Item>
                    <Form.Item name="commentPosition" label="评论位置">
                      <Radio.Group>
                        <Radio value="top">置顶</Radio>
                        <Radio value="hot">热门</Radio>
                        <Radio value="latest">最新</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </>
                );
              }
              if (mode === 'live') {
                return (
                  <>
                    <Form.Item name="liveTitle" label="直播标题">
                      <Input placeholder="请输入直播标题" />
                    </Form.Item>
                    <Form.Item name="liveDuration" label="直播时长（分钟）">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="liveStartTime" label="直播开始时间">
                      <Input placeholder="如：2025-12-05 20:00:00" />
                    </Form.Item>
                    <Form.Item name="liveEndTime" label="直播结束时间">
                      <Input placeholder="如：2025-12-05 22:00:00" />
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ContentGenerationManagement;

